import './style/Style.scss';
import 'nprogress/nprogress.css'
import 'v-calendar/lib/v-calendar.min.css';

import { Vue } from 'annotation';
import VueI18n from 'vue-i18n';
import VueRouter from 'vue-router';
import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { analytics } from "./plugins/firebase";
import VueNotification from 'vue-notification';
import VCalendar from 'v-calendar';
import Meta from 'vue-meta';
import environment from 'environment';
import NProgress from "nprogress";
import VUnicode from "v-unicode";
import VueBarcodeScanner from "vue-barcode-scanner";
import { pinia } from './stores';
import moment from 'moment';
import 'moment/dist/locale/id';

import Routes from "./config/RouteMapping";
import Authentication from "./Authentication";
import indonesianLocale from './locale/Indonesian';
import { setRouter } from './util/GlobalRouter';
import { setVm } from './util/GlobalVm';

export default class AppConfig {

  private static vm: any = null;

  private static router: any = null;

  private static i18n: VueI18n = null;

  public static init() {
    AppConfig.setVueConfig();
    AppConfig.setPlugin();
    AppConfig.setGlobalComponent();
    AppConfig.setGlobalFilter();
    AppConfig.setGlobalDirective();
    moment.locale('id');

    AppConfig.getVm().$mount("#app");
  }

  public static getI18n(): VueI18n {
    if (AppConfig.i18n === null) {
      AppConfig.i18n = new VueI18n({
        locale: environment.LOCALE,
        fallbackLocale: 'id',
        messages: {
          /*'en': englishLocale,*/
          'id': indonesianLocale
        }
      });
    }

    return AppConfig.i18n;
  }

  public static getRouter() {
    if (AppConfig.router === null) {
      AppConfig.router = new VueRouter({
        routes: Routes,
        mode: 'history',
        fallback: false,
        linkActiveClass: 'active',
        scrollBehavior() {
          return { x: 0, y: 0 };
        },
      });

      setRouter(AppConfig.router);

      AppConfig.router.beforeEach(Authentication.middleware);

      /** Animating stuff **/
      AppConfig.router.beforeResolve((to: any, from: any, next: any) => {
        NProgress.start();

        next();
      });

      AppConfig.router.afterEach((to: any, from: any) => {
        NProgress.done();

        // Log page_view event to Firebase Analytics
        if (analytics) {
          const user = Authentication.getUser();

          if (user && user.id) {
            const merchant = Authentication.getMerchant();

            // Set user dimensions
            setUserId(analytics, String(user.id));
            setUserProperties(analytics, {
              user_role: user.roles && user.roles.length > 0 ? user.roles[0] : 'unknown',
              profile_name: user.profile || 'unknown',
              wilayah: user.wilayah || (merchant ? merchant.nama : 'unknown')
            });
          }

          logEvent(analytics, 'web_page_view', {
            page_path: to.fullPath,
            page_title: to.meta?.title || 'Unknown Title',
            user_role: user && user.roles ? user.roles[0] : 'guest'
          });
        }
      });
    }

    return AppConfig.router;
  }

  public static getIsDev() {
    return process.env.NODE_ENV !== 'production';
  }

  public static getVm() {
    if (AppConfig.vm === null) {
      AppConfig.vm = new Vue({
        i18n: AppConfig.getI18n(),
        router: AppConfig.getRouter(),
        metaInfo: {
          title: ' ',
          titleTemplate: '%s',
          meta: [
            { appId: 'description', name: 'description', content: 'Dimas KHAS Kempek' }
          ],
          bodyAttrs: {
            class: 'body-pages'
          }
        },
        pinia,
        data: {
          notificationUploads: [],
          rootCurrentTab: 0
        },
        created() {
          //Store.dispatchSettingApplication();
        },
        watch: {
          '$route.fullPath': {
            immediate: true,
            handler(newValue: any, oldValue: any) {
              // bracket on route change only when user is login
              if (newValue !== undefined && oldValue !== undefined && Authentication.isLogin()) {
              }
            }
          }
        },
        methods: {
          pushUploadNotifications(percentage: number = 0, message: string = 'Upload Lampiran'): any {
            (this as any).notificationUploads.push({ percentage, message });

            return (this as any).notificationUploads.length - 1;
          },
          onToggleNotificationUpload() {
            window.$('.notification-upload-item').toggle();
          }
        }
      });

      setVm(AppConfig.vm);
    }

    return AppConfig.vm;
  }

  public static setVueConfig() {
    Vue.config.productionTip = false;
    Vue.config.devtools = false;
    Vue.config.silent = true;
  }

  public static setGlobalComponent() {
    const register = (path: string, module: any) => {
      const parts = path.split('/');
      const fileName = parts[parts.length - 1];
      const componentName = fileName.replace(/\.\w+$/, '');
      Vue.component(componentName, module.default);
    };

    const common = import.meta.glob('./views/components/common/**/*.vue', { eager: true });
    Object.entries(common).forEach(([path, mod]) => register(path, mod));

    const chart = import.meta.glob('./views/components/chart/*.vue', { eager: true });
    Object.entries(chart).forEach(([path, mod]) => register(path, mod));

    const autocomplete = import.meta.glob('./views/components/input-auto-complete/*.vue', { eager: true });
    Object.entries(autocomplete).forEach(([path, mod]) => register(path, mod));

    const defaultComps = import.meta.glob('./views/components/*.vue', { eager: true });
    Object.entries(defaultComps).forEach(([path, mod]) => register(path, mod));
  }

  public static setGlobalFilter() {
    const filters = import.meta.glob('./util/filter/**/*.ts', { eager: true });
    Object.entries(filters).forEach(([path, mod]: [string, any]) => {
      const parts = path.split('/');
      const fileName = parts[parts.length - 1];
      const filterName = fileName.replace(/\.\w+$/, '');
      Vue.filter(filterName, mod.default);
    });
  }

  public static setGlobalDirective() {
    const directives = import.meta.glob('./util/directive/**/*.ts', { eager: true });
    Object.entries(directives).forEach(([path, mod]: [string, any]) => {
      const parts = path.split('/');
      const fileName = parts[parts.length - 1];
      const directiveName = fileName.replace(/\.\w+$/, '');
      Vue.directive(directiveName.toLowerCase(), mod.default);
    });
  }

  public static setPlugin() {
    Vue.use(VCalendar, {
      firstDayOfWeek: 2,
      locale: 'id',
      popoverVisibility: 'focus'
    });

    Vue.use(Meta, {
      keyName: 'metaInfo', // key name in components
      attribute: 'dimas-khas-meta', // uses to add in the tags to observes
      ssrAttribute: 'dimas-khas-meta-server', // uses to determine meta info has already been server-rendered
      tagIDKeyName: 'dimas-khas-id' // uses to determine whether to overwrite or append a tag (as ID)
    });

    Vue.use(VueRouter);
    Vue.use(VueI18n);
    Vue.use(VueNotification);
    Vue.use(VUnicode);
    Vue.use(VueBarcodeScanner);
  }
}
